/**
 * Bruce design tokens + the global MUI theme.
 *
 * The dark palette was born on the Trading Desk dashboard (9/25) as
 * page-scoped tokens; since 10/25 it IS the app theme — every page,
 * dialog and table renders on it. Pages reach for `C` when they need a
 * raw color (charts, chips), and for MUI palette keys everywhere else.
 */
import { createTheme, alpha } from '@mui/material/styles';

export const C = {
  bg: '#0B0E14', panel: '#11151D', panel2: '#151A24',
  line: 'rgba(255,255,255,.07)', line2: 'rgba(255,255,255,.12)',
  ink: '#E7EBF3', ink2: '#98A2B6', ink3: '#5C6678',
  accent: '#3987E5', pos: '#35C48D', neg: '#E66767', warn: '#C98500',
  s: {
    IRON_CONDOR: '#3987E5', BREADTH_SPREAD: '#D95926',
    BREADTH_PUT: '#8B5CF6', BREADTH_DIP: '#0D9488', TREND_OPTION: '#C98500',
  } as Record<string, string>,
  spy: '#D95926', nasdaq: '#8B5CF6',   // validated categorical slots 2/3
  neutral: '#2A3140',
};

export const MONO = "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace";
export const SANS = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

/** Icon rail width (desktop). Layout and any full-bleed page share it. */
export const RAIL_WIDTH = 64;

export const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: C.accent, light: '#6AA6EE', dark: '#2A6BBE' },
    secondary: { main: C.nasdaq },
    success: { main: C.pos },
    error: { main: C.neg },
    warning: { main: C.warn },
    info: { main: C.accent },
    background: { default: C.bg, paper: C.panel },
    divider: C.line2,
    text: { primary: C.ink, secondary: C.ink2, disabled: C.ink3 },
    action: {
      hover: 'rgba(255,255,255,.04)',
      selected: alpha(C.accent, 0.18),
      disabledBackground: 'rgba(255,255,255,.06)',
    },
  },
  shape: { borderRadius: 10 },
  typography: {
    fontFamily: SANS,
    h1: { fontWeight: 700 }, h2: { fontWeight: 700 }, h3: { fontWeight: 600 },
    h4: { fontWeight: 600, fontSize: '1.5rem' }, h5: { fontWeight: 600 }, h6: { fontWeight: 600 },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: C.bg, color: C.ink, colorScheme: 'dark' },
        '*::-webkit-scrollbar': { width: 8, height: 8 },
        '*::-webkit-scrollbar-thumb': { background: C.neutral, borderRadius: 8 },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { backgroundImage: 'none', border: `1px solid ${C.line}` },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: { borderRadius: 14, boxShadow: 'none', border: `1px solid ${C.line}` },
      },
    },
    MuiButton: {
      styleOverrides: { root: { borderRadius: 8 } },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottom: `1px solid ${C.line}`, fontVariantNumeric: 'tabular-nums' },
        head: {
          fontSize: 11, fontWeight: 600, letterSpacing: '.07em', textTransform: 'uppercase',
          color: C.ink3, borderBottom: `1px solid ${C.line2}`, whiteSpace: 'nowrap',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: { '&:hover td': { backgroundColor: 'rgba(255,255,255,.025)' } },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#1A2130', color: C.ink, border: `1px solid ${C.line2}`,
          fontSize: 12.5, fontWeight: 500, padding: '6px 10px', borderRadius: 8,
        },
        arrow: { color: '#1A2130', '&::before': { border: `1px solid ${C.line2}` } },
      },
    },
    MuiChip: {
      styleOverrides: { root: { fontWeight: 600 } },
    },
    MuiDialog: {
      styleOverrides: { paper: { backgroundColor: C.panel2, border: `1px solid ${C.line2}` } },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: { borderColor: C.line2 },
        root: { '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: C.ink3 } },
      },
    },
    MuiAlert: {
      styleOverrides: { root: { border: `1px solid ${C.line2}` } },
    },
    MuiDrawer: {
      styleOverrides: { paper: { backgroundColor: C.panel, backgroundImage: 'none' } },
    },
    MuiAppBar: {
      styleOverrides: {
        root: { backgroundColor: C.panel, backgroundImage: 'none', boxShadow: 'none', borderBottom: `1px solid ${C.line}` },
      },
    },
  },
});
